import * as Sentry from '@sentry/browser'
import { type FpMembershipQuery } from 'graphQL/hatostore/generate/operations'
import { useDispatch } from 'react-redux'
import { setLoadingMembership, setMembership, setMembershipQRCode } from 'store/slices/foodpassionCrmSlice'
import QRCode from 'qrcode'
import { displayPoint, displayPrice } from 'helpers'
import { DeliveryFpMembershipTierEnum } from 'graphQL/hatostore/generate/operations'
import dayjs from 'dayjs'
import { FoodpassionExternalPage } from 'variables/foodpassion-crm'
import { isDevelopment } from 'variables/environment'

const useFoodpassionCrm = () => {
  const dispatch = useDispatch()

  const initFoodpassionCrmState = async (fpMembership: FpMembershipQuery['fpMembership']) => {
    try {
      if (!fpMembership) {
        redirectToTermsAndConditions()
        return
      } else if (!fpMembership.acceptedLatestConsent) {
        redirectToConsent()
        return
      }

      const fullName =
        fpMembership.firstName || fpMembership.lastName ? `${fpMembership.firstName} ${fpMembership.lastName}` : ''
      const qrCode = (await getQRCodeUrl(fpMembership.cardNumber)) || ''
      const pointDetail = fpMembership.pointDetail.map(({ value, expired }) => ({
        value: displayPoint(value),
        expired: dayjs(expired).format('DD/MM/YYYY'),
      }))

      dispatch(
        setMembership({
          ...fpMembership,
          point: displayPoint(fpMembership.point),
          membershipName: getMembershipNameByTier(fpMembership.currentTierName),
          tierImageUrl: getFpTierImage(fpMembership.currentTierName),
          nextTierName: getMembershipNameByTier(fpMembership.nextTierName),
          nextTierSpending: displayPrice(fpMembership.nextTierSpending),
          fullName,
          pointDetail,
        })
      )
      dispatch(setMembershipQRCode(qrCode))
      handleSetLoadingMembership(false)
    } catch (error) {
      !isDevelopment && Sentry.captureException(new Error(`Error from init foodpassion crm: ${JSON.stringify(error)}`))
    } finally {
      handleSetLoadingMembership(false)
    }
  }

  const handleSetLoadingMembership = (loading: boolean) => {
    dispatch(setLoadingMembership(loading))
  }

  const redirectToTermsAndConditions = () => {
    window.location.href = `${FoodpassionExternalPage.TERM_AND_CONDITION}?callback=${window.location.href}`
  }

  const redirectToProfile = () => {
    window.location.href = `${FoodpassionExternalPage.PROFILE}?callback=${window.location.href}`
  }

  const redirectToBenefit = () => {
    window.location.href = FoodpassionExternalPage.BENEFITS
  }

  const redirectToConsent = () => {
    window.location.href = `${FoodpassionExternalPage.CONSENT}?callback=${window.location.href}`
  }

  return {
    initFoodpassionCrmState,
    handleSetLoadingMembership,
    redirectToBenefit,
    redirectToProfile,
  }
}

export default useFoodpassionCrm

async function getQRCodeUrl(text: string) {
  try {
    const result = await QRCode.toDataURL(text)
    return result
  } catch (err) {
    console.error(err)
    return ''
  }
}

export const getFpTierImage = (tier: DeliveryFpMembershipTierEnum) => {
  switch (tier) {
    case DeliveryFpMembershipTierEnum.FoodPassionFriend:
      return `/assets/gon/memberships/gon-member-friend.png`
    case DeliveryFpMembershipTierEnum.FoodPassionBuddy:
      return `/assets/gon/memberships/gon-member-buddy.png`
    case DeliveryFpMembershipTierEnum.FoodPassionFamily:
      return `/assets/gon/memberships/gon-member-family.png`
    case DeliveryFpMembershipTierEnum.FoodPassionSoulmate:
      return `/assets/gon/memberships/gon-member-soulmate.png`
    default:
      return ''
  }
}

export const getMembershipNameByTier = (tier: DeliveryFpMembershipTierEnum) => {
  switch (tier) {
    case DeliveryFpMembershipTierEnum.FoodPassionFriend:
      return 'Friend'
    case DeliveryFpMembershipTierEnum.FoodPassionBuddy:
      return 'Buddy'
    case DeliveryFpMembershipTierEnum.FoodPassionFamily:
      return 'Family'
    case DeliveryFpMembershipTierEnum.FoodPassionSoulmate:
      return 'Soulmate'
    default:
      return ''
  }
}
